import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  LogOut, Users, CheckCircle2, XCircle, Ban, Eye, Trash2, Search, Download, FileText, Handshake,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/lib/constants";
import { PartnersManager } from "@/components/admin/PartnersManager";
import { AdminMessaging } from "@/components/admin/AdminMessaging";

const STATUS = [
  { key: "preinscrit", label: "Pré-inscrit", color: "text-neutral-300" },
  { key: "retenu", label: "Retenu", color: "text-theatre-gold" },
  { key: "ecarte", label: "Écarté", color: "text-neutral-400" },
  { key: "refuse", label: "A refusé", color: "text-theatre-red" },
];
const statusLabel = (k) => STATUS.find((s) => s.key === k)?.label || k;

const FILTERS = [{ key: "all", label: "Tous" }, ...STATUS.map((s) => ({ key: s.key, label: s.label }))];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [view, setView] = useState("candidatures");

  const loadStats = useCallback(() => {
    api.get("/candidates/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/candidates", { params }).then(({ data }) => setCandidates(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const saveCandidate = async (id, payload) => {
    try {
      const { data } = await api.patch(`/candidates/${id}/status`, payload);
      toast.success("Candidat mis à jour.");
      setCandidates((cs) => cs.map((c) => (c.id === id ? data : c)));
      setSelected(data);
      loadStats();
      if (filter !== "all" && payload.status && payload.status !== filter) load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/candidates/${toDelete.id}`);
      toast.success("Candidat supprimé.");
      setCandidates((cs) => cs.filter((c) => c.id !== toDelete.id));
      loadStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setToDelete(null);
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/candidates/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "candidats-viva-artist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec de l'export CSV.");
    }
  };

  const openDossier = async (id) => {
    try {
      const { data } = await api.get(`/candidates/${id}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Impossible d'ouvrir le dossier.");
    }
  };

  const filtered = candidates.filter((c) => {
    const q = search.toLowerCase();
    return `${c.prenom} ${c.nom} ${c.email} ${c.parti} ${c.region}`.toLowerCase().includes(q);
  });

  const statCards = [
    { label: "Total", value: stats?.total ?? 0, icon: Users, color: "text-white" },
    { label: "Retenus", value: stats?.retenu ?? 0, icon: CheckCircle2, color: "text-theatre-gold" },
    { label: "Écartés", value: stats?.ecarte ?? 0, icon: XCircle, color: "text-neutral-400" },
    { label: "Ont refusé", value: stats?.refuse ?? 0, icon: Ban, color: "text-theatre-red" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 bg-black px-6 py-8">
        <div className="flex items-center gap-3 mb-12">
          <img src={IMAGES.logo} alt="Viva Artist" className="h-10 w-10 object-contain" />
          <span className="font-serif text-lg">VIVA <span className="text-theatre-gold">ARTIST</span></span>
        </div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600 mb-4">Menu</p>
        <button
          data-testid="nav-view-candidatures"
          onClick={() => setView("candidatures")}
          className={`flex items-center gap-2 text-sm pl-3 mb-2 border-l-2 transition-colors duration-200 ${view === "candidatures" ? "text-theatre-gold border-theatre-gold" : "text-neutral-400 border-transparent hover:text-white"}`}
        >
          <Users size={15} /> Candidatures
        </button>
        <button
          data-testid="nav-view-partenaires"
          onClick={() => setView("partenaires")}
          className={`flex items-center gap-2 text-sm pl-3 mb-2 border-l-2 transition-colors duration-200 ${view === "partenaires" ? "text-theatre-gold border-theatre-gold" : "text-neutral-400 border-transparent hover:text-white"}`}
        >
          <Handshake size={15} /> Partenaires
        </button>
        <div className="mt-auto">
          <p className="text-xs text-neutral-500 mb-1">Connecté</p>
          <p className="text-sm text-neutral-300 truncate">{user?.email}</p>
          <button data-testid="admin-logout-btn" onClick={doLogout} className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-theatre-red transition-colors duration-200">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 px-6 lg:px-10 py-8 custom-scroll overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl">{view === "partenaires" ? "Gestion des partenaires" : "Gestion des candidatures"}</h1>
            <p className="text-sm text-neutral-500 mt-1">« Au théâtre présidentiel ce soir ! » — Présidence 2027</p>
          </div>
          <button data-testid="admin-logout-btn-top" onClick={doLogout} className="lg:hidden flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-400">
            <LogOut size={14} /> Sortir
          </button>
        </div>

        {view === "partenaires" && <PartnersManager />}

        {view === "candidatures" && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#111] border border-white/10 p-5" data-testid={`stat-${s.label}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.15em] text-neutral-500">{s.label}</span>
                <s.icon className={s.color} size={18} />
              </div>
              <p className={`mt-3 font-serif text-3xl ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between mb-5">
          <div className="flex flex-wrap gap-2" data-testid="admin-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-200 ${
                  filter === f.key ? "bg-theatre-red border-theatre-red text-white" : "border-white/15 text-neutral-400 hover:border-theatre-gold/50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600" size={15} />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="bg-[#111] border border-white/15 text-white text-sm pl-9 pr-4 h-10 w-full sm:w-64 focus:outline-none focus:border-theatre-gold"
            />
          </div>
          <button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            className="flex items-center gap-2 px-4 h-10 border border-theatre-gold/50 text-theatre-gold hover:bg-theatre-gold hover:text-black text-xs uppercase tracking-[0.15em] transition-colors duration-200 whitespace-nowrap"
          >
            <Download size={15} /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/10 overflow-x-auto custom-scroll" data-testid="candidates-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-[#111] text-neutral-500 text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em]">Candidat</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em]">Parti · Région</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em]">Statut</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em]">Publié</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-white/10 hover:bg-white/[0.03]" data-testid={`candidate-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-white">{c.prenom} {c.nom}</p>
                    <p className="text-xs text-neutral-500">{c.email}</p>
                  </td>
                  <td className="px-4 py-3 text-neutral-400">{c.parti || "Indépendant"} · {c.region}</td>
                  <td className="px-4 py-3">
                    <span className={STATUS.find((s) => s.key === c.status)?.color}>{statusLabel(c.status)}</span>
                  </td>
                  <td className="px-4 py-3">{c.published ? <span className="text-theatre-gold text-xs">● Publié</span> : <span className="text-neutral-600 text-xs">Privé</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`view-${c.id}`} onClick={() => setSelected(c)} className="p-2 border border-white/15 hover:border-theatre-gold text-neutral-300 hover:text-theatre-gold transition-colors duration-200">
                        <Eye size={15} />
                      </button>
                      <button data-testid={`delete-${c.id}`} onClick={() => setToDelete(c)} className="p-2 border border-white/15 hover:border-theatre-red text-neutral-300 hover:text-theatre-red transition-colors duration-200">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-neutral-600" data-testid="candidates-empty-row">Aucun candidat.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0c0c0c] border border-theatre-gold/25 text-white max-w-lg max-h-[92vh] overflow-y-auto custom-scroll" data-testid="candidate-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{selected.prenom} {selected.nom}</DialogTitle>
                <DialogDescription className="text-neutral-500">Dossier de candidature — {statusLabel(selected.status)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm max-h-[45vh] overflow-y-auto custom-scroll pr-2">
                <Row label="Email" value={selected.email} />
                <Row label="Téléphone" value={selected.telephone} />
                <Row label="Parti / Mouvement" value={selected.parti || "Indépendant"} />
                <Row label="Profession" value={selected.profession} />
                <Row label="Région" value={selected.region} />
                <div>
                  <p className="text-neutral-500 text-xs uppercase tracking-[0.15em] mb-1">Vision</p>
                  <p className="text-neutral-300 leading-relaxed">{selected.vision}</p>
                </div>
                {selected.programme && (
                  <div>
                    <p className="text-neutral-500 text-xs uppercase tracking-[0.15em] mb-1">Programme</p>
                    <p className="text-neutral-300 leading-relaxed">{selected.programme}</p>
                  </div>
                )}
                {selected.dossier_file_id && (
                  <div>
                    <p className="text-neutral-500 text-xs uppercase tracking-[0.15em] mb-2">Dossier PDF</p>
                    <button
                      data-testid="open-dossier-btn"
                      onClick={() => openDossier(selected.id)}
                      className="inline-flex items-center gap-2 border border-theatre-gold/50 text-theatre-gold hover:bg-theatre-gold hover:text-black px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-200"
                    >
                      <FileText size={15} /> {selected.dossier_filename || "Ouvrir le dossier"}
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-white/10 pt-4 space-y-4">
                <div>
                  <p className="text-neutral-500 text-xs uppercase tracking-[0.15em] mb-2">Décision</p>
                  <div className="grid grid-cols-2 gap-2">
                    {STATUS.map((s) => (
                      <button
                        key={s.key}
                        data-testid={`set-status-${s.key}`}
                        onClick={() => saveCandidate(selected.id, { status: s.key })}
                        className={`py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-200 ${
                          selected.status === s.key ? "bg-theatre-red border-theatre-red text-white" : "border-white/15 text-neutral-400 hover:border-theatre-gold/50"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-500 text-xs uppercase tracking-[0.15em]">Note publique (optionnelle)</Label>
                  <Textarea
                    data-testid="candidate-note"
                    defaultValue={selected.note}
                    key={selected.id + selected.note}
                    onBlur={(e) => e.target.value !== selected.note && saveCandidate(selected.id, { status: selected.status, note: e.target.value })}
                    rows={2}
                    className="bg-black/50 border-white/15 text-white rounded-none focus-visible:ring-theatre-gold"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="pub" className="text-sm text-neutral-300">Publier sur le site public</Label>
                  <Switch
                    id="pub"
                    data-testid="publish-toggle"
                    checked={selected.published}
                    onCheckedChange={(v) => saveCandidate(selected.id, { status: selected.status, published: v })}
                    className="data-[state=checked]:bg-theatre-red"
                  />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <AdminMessaging candidateId={selected.id} />
              </div>

              <DialogFooter>
                <button data-testid="close-detail" onClick={() => setSelected(null)} className="border border-white/15 px-5 py-2 text-xs uppercase tracking-[0.2em] text-neutral-300 hover:border-theatre-gold hover:text-theatre-gold transition-colors duration-200">
                  Fermer
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0c0c0c] border border-theatre-red/30 text-white" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Supprimer ce candidat ?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Cette action est irréversible. {toDelete?.prenom} {toDelete?.nom} sera définitivement retiré.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-neutral-300 hover:bg-white/5 rounded-none">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={confirmDelete} className="bg-theatre-red hover:bg-theatre-redhover text-white rounded-none">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-neutral-500 text-xs uppercase tracking-[0.15em]">{label}</span>
    <span className="text-neutral-200 text-right">{value}</span>
  </div>
);
